import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import RaceDetailPage from '../raceDetail/RaceDetailPage';
import PredictionServiceNav from './PredictionServiceNav';

function PreviewRaceDashboardPage({
  activeServiceKey,
  selectorTitle,
  selectorEyebrow = 'Race Selector',
  previewNotice,
  raceNotice,
  helpTitle = 'AI指標について',
  helpBody,
  availableDates,
  racesByDate,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedDate, setSelectedDate] = useState(availableDates[0] || '');
  const [selectedRaceMeta, setSelectedRaceMeta] = useState(null);
  const [selectorExpanded, setSelectorExpanded] = useState(true);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);

  const raceList = useMemo(() => racesByDate[selectedDate] || [], [racesByDate, selectedDate]);

  useEffect(() => {
    if (!selectedDate && availableDates.length > 0) {
      setSelectedDate(availableDates[0]);
    }
  }, [availableDates, selectedDate]);

  useEffect(() => {
    if (!raceList.length) {
      setSelectedRaceMeta(null);
      return;
    }

    setSelectedRaceMeta((current) => {
      if (current && raceList.some((race) => race.race_key === current.race_key)) {
        return current;
      }
      return raceList[0];
    });
  }, [raceList]);

  useEffect(() => {
    if (!isMobile) {
      setSelectorExpanded(true);
    }
  }, [isMobile]);

  const detailModel = selectedRaceMeta?.detailModel || null;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background:
          'radial-gradient(circle at 15% 0%, rgba(43,132,255,0.22), transparent 28%), radial-gradient(circle at 90% 10%, rgba(50,255,226,0.14), transparent 24%), linear-gradient(180deg, #02060d 0%, #07111d 46%, #03070f 100%)',
        color: '#eef8ff',
        fontFamily: '"Avenir Next", "Yu Gothic", "Hiragino Sans", sans-serif',
      }}
    >
      <Box sx={{ maxWidth: 1440, mx: 'auto', px: { xs: 2, sm: 3, lg: 4 }, py: { xs: 3, sm: 4 } }}>
        <Stack spacing={3}>
          <PredictionServiceNav activeKey={activeServiceKey} />

          <Paper
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: 4,
              color: 'inherit',
              border: '1px solid rgba(92, 160, 219, 0.2)',
              background:
                'linear-gradient(180deg, rgba(10,24,41,0.92), rgba(4,10,20,0.95))',
              boxShadow: '0 24px 60px rgba(0, 0, 0, 0.28)',
            }}
          >
            <Stack spacing={2.5}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
                spacing={2}
              >
                <Box>
                  <Typography
                    sx={{
                      color: '#71dbff',
                      fontSize: 12,
                      letterSpacing: '0.24em',
                      textTransform: 'uppercase',
                    }}
                  >
                    {selectorEyebrow}
                  </Typography>
                  <Typography sx={{ mt: 1, fontSize: { xs: 24, sm: 30 }, fontWeight: 700 }}>
                    {selectorTitle}
                  </Typography>
                  {isMobile && selectedRaceMeta?.race_name && !selectorExpanded && (
                    <Typography sx={{ mt: 1, color: 'rgba(203, 229, 245, 0.72)', fontSize: 13 }}>
                      選択中: {selectedRaceMeta.race_name}
                    </Typography>
                  )}
                </Box>

                {isMobile ? (
                  <Button
                    variant="outlined"
                    onClick={() => setSelectorExpanded((prev) => !prev)}
                    endIcon={selectorExpanded ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                    sx={{
                      alignSelf: 'stretch',
                      borderRadius: 999,
                      color: '#9ed7ff',
                      borderColor: 'rgba(92, 160, 219, 0.22)',
                      bgcolor: 'rgba(8, 18, 32, 0.72)',
                    }}
                  >
                    {selectorExpanded ? '選択エリアを隠す' : '選択エリアを表示'}
                  </Button>
                ) : (
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <FormControl
                      size="small"
                      sx={{
                        minWidth: 190,
                        '& .MuiOutlinedInput-root': {
                          color: '#ecf9ff',
                          bgcolor: 'rgba(7, 17, 31, 0.86)',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(92, 160, 219, 0.26)',
                        },
                        '& .MuiSvgIcon-root': {
                          color: '#9ed7ff',
                        },
                      }}
                    >
                      <InputLabel id={`${activeServiceKey}-date-select-label`} sx={{ color: '#9ed7ff' }}>
                        日付選択
                      </InputLabel>
                      <Select
                        labelId={`${activeServiceKey}-date-select-label`}
                        value={selectedDate}
                        label="日付選択"
                        onChange={(event) => setSelectedDate(event.target.value)}
                      >
                        {availableDates.map((date) => (
                          <MenuItem key={date} value={date}>
                            {date}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <IconButton
                      onClick={() => setHelpDialogOpen(true)}
                      sx={{
                        color: '#9ed7ff',
                        border: '1px solid rgba(92, 160, 219, 0.22)',
                        bgcolor: 'rgba(8, 18, 32, 0.72)',
                      }}
                    >
                      <HelpOutlineIcon />
                    </IconButton>
                  </Stack>
                )}
              </Stack>

              <Collapse in={!isMobile || selectorExpanded} timeout="auto" unmountOnExit>
                <Stack spacing={2.5}>
                  {isMobile && (
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <FormControl
                        size="small"
                        sx={{
                          minWidth: 190,
                          flex: 1,
                          '& .MuiOutlinedInput-root': {
                            color: '#ecf9ff',
                            bgcolor: 'rgba(7, 17, 31, 0.86)',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(92, 160, 219, 0.26)',
                          },
                          '& .MuiSvgIcon-root': {
                            color: '#9ed7ff',
                          },
                        }}
                      >
                        <InputLabel id={`${activeServiceKey}-date-select-label-mobile`} sx={{ color: '#9ed7ff' }}>
                          日付選択
                        </InputLabel>
                        <Select
                          labelId={`${activeServiceKey}-date-select-label-mobile`}
                          value={selectedDate}
                          label="日付選択"
                          onChange={(event) => setSelectedDate(event.target.value)}
                        >
                          {availableDates.map((date) => (
                            <MenuItem key={date} value={date}>
                              {date}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <IconButton
                        onClick={() => setHelpDialogOpen(true)}
                        sx={{
                          color: '#9ed7ff',
                          border: '1px solid rgba(92, 160, 219, 0.22)',
                          bgcolor: 'rgba(8, 18, 32, 0.72)',
                        }}
                      >
                        <HelpOutlineIcon />
                      </IconButton>
                    </Stack>
                  )}

                  <Alert
                    severity="info"
                    sx={{
                      color: '#eaf8ff',
                      bgcolor: alpha('#2a7dff', 0.12),
                      border: '1px solid rgba(100, 171, 255, 0.2)',
                    }}
                  >
                    {previewNotice}
                  </Alert>

                  {raceNotice && (
                    <Alert
                      severity="warning"
                      sx={{
                        color: '#fff5d6',
                        bgcolor: alpha('#ffb347', 0.12),
                        border: '1px solid rgba(255, 187, 86, 0.2)',
                      }}
                    >
                      {raceNotice}
                    </Alert>
                  )}

                  <Box
                    sx={{
                      display: 'grid',
                      gap: 1.25,
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(auto-fit, minmax(220px, 1fr))',
                      },
                    }}
                  >
                    {raceList.map((race) => {
                      const isSelected = selectedRaceMeta?.race_key === race.race_key;
                      return (
                        <Button
                          key={race.race_key}
                          onClick={() => {
                            setSelectedRaceMeta(race);
                            if (isMobile) {
                              setSelectorExpanded(false);
                            }
                          }}
                          sx={{
                            width: '100%',
                            minWidth: 0,
                            px: 2,
                            py: 1.25,
                            borderRadius: 3,
                            textTransform: 'none',
                            alignItems: 'flex-start',
                            border: '1px solid',
                            borderColor: isSelected
                              ? 'rgba(108, 247, 255, 0.42)'
                              : 'rgba(92, 160, 219, 0.18)',
                            background: isSelected
                              ? 'linear-gradient(135deg, rgba(22,111,199,0.94), rgba(14,54,109,0.94))'
                              : 'linear-gradient(180deg, rgba(9,20,35,0.88), rgba(6,14,25,0.88))',
                            color: '#eef8ff',
                            boxShadow: isSelected ? '0 0 22px rgba(76, 186, 255, 0.18)' : 'none',
                          }}
                        >
                          <Box sx={{ textAlign: 'left' }}>
                            <Typography sx={{ fontWeight: 700 }}>{race.race_name}</Typography>
                            <Typography sx={{ mt: 0.5, fontSize: 12, color: 'rgba(200, 228, 245, 0.68)' }}>
                              {[
                                (race.start_time || race.start_time_live)
                                  ? `発走 ${race.start_time || race.start_time_live}`
                                  : null,
                                race.surface_label,
                                race.roughness_raw ? `荒れ度 ${race.roughness_raw}` : null,
                                race.confidence_raw ? `自信度 ${race.confidence_raw}` : null,
                              ]
                                .filter(Boolean)
                                .join(' / ') || 'AIデータ待機中'}
                            </Typography>
                          </Box>
                        </Button>
                      );
                    })}
                  </Box>
                </Stack>
              </Collapse>
            </Stack>
          </Paper>

          {detailModel ? (
            <RaceDetailPage model={detailModel} />
          ) : (
            <Paper
              sx={{
                p: { xs: 3, sm: 5 },
                borderRadius: 4,
                textAlign: 'center',
                color: '#eef8ff',
                border: '1px solid rgba(92, 160, 219, 0.18)',
                background:
                  'linear-gradient(180deg, rgba(10,24,41,0.84), rgba(4,10,20,0.9))',
              }}
            >
              <Typography
                sx={{
                  color: '#71dbff',
                  fontSize: 12,
                  letterSpacing: '0.24em',
                  textTransform: 'uppercase',
                }}
              >
                Analysis Standby
              </Typography>
              <Typography sx={{ mt: 1.5, fontSize: { xs: 22, sm: 28 }, fontWeight: 700 }}>
                レースを選択するとAI分析画面を表示します
              </Typography>
            </Paper>
          )}
        </Stack>
      </Box>

      <Dialog open={helpDialogOpen} onClose={() => setHelpDialogOpen(false)}>
        <DialogTitle sx={{ fontWeight: 700 }}>{helpTitle}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">{helpBody}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpDialogOpen(false)}>閉じる</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PreviewRaceDashboardPage;
